import { FC, ReactElement, ReactNode, useEffect, useState } from 'react';

import {
  Box,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  CSS,
  Icon,
  IconButton,
  Overlay,
  Popover,
  Typography,
} from '@project44/manifest-ds';

import { scopedT } from '../../utils/functions/scopedT';

const t = scopedT();

interface Props {
  open: boolean;
  cancelText?: string;
  children?: ReactNode | ReactNode[];
  closable?: boolean;
  css?: CSS;
  cardHeaderCss?: CSS;
  cardFooterCss?: CSS;
  footer?: 'none' | 'default' | ReactNode | ReactNode[];
  height?: number | string;
  okText?: string;
  overlayClosable?: boolean;
  title?: string | ReactNode;
  width?: number | string;
  onCancel?: () => void;
  onOk?: () => void;
}

/**
 * Modal component
 * Uses Manifest's Overlay and Popovers components
 * that are creating it through React Portal
 * By default it has a Close, Cancel and OK buttons
 * @param {boolean} props.open Whether the modal dialog is visible or not.
 * @param {string} props.cancelText Text of the Cancel button
 * @param {ReactNode | ReactNode[]} props.children Main content of the Modal
 * @param {boolean} props.closable Whether a close (x) button is visible on top right of the modal dialog or not
 * @param {CSS} props.css CSS styles for Modal element
 * @param {CSS} props.cardHeaderCss CSS styles for the Card Header
 * @param {CSS} props.cardFooterCss CSS styles for the Card Footer
 * @param {'none' | 'default' | ReactNode | ReactNode[]} props.footer Footer content, set as footer='none' when you don't need default buttons
 * @param {number | string} props.height Height of the Modal element
 * @param {string} props.okText Text of the OK button
 * @param {boolean} props.overlayClosable Whether to close the modal dialog when the overlay (area outside the modal) is clicked
 * @param {string} props.title The modal dialog's title
 * @param {number | string} props.width Width of the Modal element
 * @param {Function} props.onCancel Specify a function that will be called when a user clicks overlay, close button on top right or Cancel button
 * @param {Function} props.onOk Specify a function that will be called when a user clicks the OK button
 * @returns {ReactElement} Modal component
 */
const Modal: FC<Props> = ({
  open,
  cancelText,
  children,
  closable = true,
  css,
  cardHeaderCss,
  cardFooterCss,
  footer = 'default',
  height,
  okText,
  overlayClosable = true,
  title,
  width,
  onCancel,
  onOk,
}: Props): ReactElement => {
  // only for animation
  const [openAnim, setOpenAnim] = useState<boolean>(open);
  useEffect(() => {
    setOpenAnim(open);
  }, [open]);

  const getTitle = () => {
    if (!title) return null;

    return typeof title === 'string' ? (
      <Typography as="h2" variant="title">
        {title}
      </Typography>
    ) : (
      title
    );
  };

  return (
    <Overlay isOpen={open}>
      <Popover
        isOpen={open}
        css={{
          alignItems: 'center',
          bgColor: 'rgba(9, 21, 33, 0.9)',
          display: 'flex',
          h: '100%',
          justifyContent: 'center',
          left: 0,
          opacity: open ? 1 : 0,
          position: 'fixed',
          top: 0,
          transition: 'opacity ease-out 150ms',
          w: '100%',
          zIndex: 1050,
        }}
      >
        <Box
          data-testid="modal-handle-close-overlay"
          css={{
            h: '100%',
            position: 'absolute',
            w: '100%',
          }}
          onClick={overlayClosable ? onCancel : undefined}
        ></Box>
        <Card
          data-testid="modal"
          css={{
            bgColor: '$background-primary',
            borderRadius: 4,
            boxShadow: '$large',
            display: 'flex',
            flexDirection: 'column',
            height,
            m: '$large',
            maxWidth: !width ? 560 : undefined,
            overflow: 'hidden',
            transform: `translate3d(0px, ${openAnim ? 0 : 140}px, 0px)`,
            transition: 'transform ease-out 150ms',
            width,
            zIndex: 1,
            ...css,
          }}
        >
          {(closable || title) && (
            <CardHeader
              css={{
                alignContent: 'center',
                bgColor: '$background-primary',
                borderBottom: '1px solid $border-primary',
                justifyContent: 'space-between',
                p: '$large',
                ...cardHeaderCss,
              }}
            >
              {getTitle()}
              {closable && (
                <IconButton
                  data-testid="modal-close-button"
                  variant="tertiary"
                  onClick={onCancel}
                  css={{ my: '-$small' }}
                >
                  <Icon icon="close" css={{ color: '$text-tertiary' }} />
                </IconButton>
              )}
            </CardHeader>
          )}
          <CardBody
            css={{
              overflowY: 'auto',
              p: '$large',
            }}
          >
            {children}
          </CardBody>
          {footer === 'none' ? null : (
            <CardFooter
              data-testid="modal-footer"
              css={{
                bgColor: '$background-secondary',
                borderTop: '1px solid $border-primary',
                gap: '$small',
                justifyContent: 'flex-end',
                p: '$large',
                ...cardFooterCss,
              }}
            >
              {footer === 'default' ? (
                <>
                  <Button variant="secondary" onClick={onCancel}>
                    {cancelText || t('modal.cancel')}
                  </Button>
                  <Button onClick={onOk}>{okText || t('modal.ok')}</Button>
                </>
              ) : (
                footer
              )}
            </CardFooter>
          )}
        </Card>
      </Popover>
    </Overlay>
  );
};

export default Modal;
